import React, { useState, useMemo } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { HelpCircle, Search, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface FAQItem {
  category: string;
  question: string;
  answer: string;
}

const defaultFaqs: FAQItem[] = [
  {
    category: "Général",
    question: "Qu'est-ce qu'ECATHEBA ?",
    answer: "ECATHEBA (École Cathédrale de Théologie de Bafoussam) est une plateforme d'apprentissage en ligne dédiée à la formation théologique. Notre mission est de former des fidèles bien équipés pour une foi vivante, agissante et efficace."
  },
  {
    category: "Général",
    question: "Comment fonctionne la plateforme d'apprentissage ?",
    answer: "Notre plateforme d'apprentissage est intuitive et accessible. Vous pouvez vous inscrire aux cours, suivre les leçons à votre rythme, participer aux discussions et passer les évaluations. Tous vos progrès sont sauvegardés automatiquement."
  },
  {
    category: "Paiement",
    question: "Quels sont les moyens de paiement acceptés ?",
    answer: "Nous acceptons plusieurs moyens de paiement : cartes bancaires, Mobile Money (Orange Money, MTN Mobile Money, etc.), PayPal, et les transferts bancaires. Tous les paiements sont sécurisés."
  },
  {
    category: "Paiement",
    question: "Les paiements sont-ils sécurisés ?",
    answer: "Oui, tous nos paiements sont sécurisés. Nous utilisons le cryptage SSL et collaborons avec des prestataires de paiement reconnus pour garantir la sécurité de vos transactions."
  },
  {
    category: "Cours",
    question: "Comment s'inscrire à un cours ?",
    answer: "Pour vous inscrire à un cours, créez un compte étudiant et parcourez notre catalogue de formations. Cliquez sur le cours qui vous intéresse et suivez le processus d'inscription. Certains cours sont gratuits, d'autres nécessitent un paiement."
  },
  {
    category: "Cours",
    question: "Quels types de cours sont disponibles ?",
    answer: "Nous proposons diverses formations théologiques : théologie systématique, études bibliques, histoire de l'Église, théologie pratique, éthique chrétienne, etc. Nos cours s'adaptent à tous les niveaux, du débutant au niveau avancé."
  },
  {
    category: "Technique",
    question: "Comment récupérer mon mot de passe ?",
    answer: "Cliquez sur 'Mot de passe oublié' sur la page de connexion. Vous recevrez un email avec les instructions pour réinitialiser votre mot de passe."
  },
  {
    category: "Technique",
    question: "L'application est-elle disponible sur mobile ?",
    answer: "Oui, ECATHEBA est entièrement responsive et fonctionne sur tous les appareils : ordinateurs, tablettes et smartphones. Aucune installation n'est nécessaire, vous pouvez accéder à vos cours depuis n'importe quel navigateur."
  }
];

export const Faq: React.FC = () => {
  const { tenant, previewConfig } = useTenantContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Tous');
  const [openQuestions, setOpenQuestions] = useState<number[]>([]);

  // Thème FAQ (preview > tenant)
  const faqTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'HelpCircle', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  // Config FAQ: preview > tenant > defaults
  const faqConfig = useMemo(() => {
    const pages = (previewConfig as any)?.pages ?? (tenant?.config as any)?.pages ?? {};
    const faq = (pages as any)?.faq ?? {};
    const items: FAQItem[] = Array.isArray(faq.items) && faq.items.length > 0 ? (faq.items as FAQItem[]) : defaultFaqs;
    return {
      headerTitle: faq.headerTitle || 'Questions Fréquentes',
      headerSubtitle: faq.headerSubtitle || `Trouvez rapidement les réponses à vos questions sur ${tenant?.name || 'notre site'}`,
      searchPlaceholder: faq.searchPlaceholder || 'Rechercher une question...',
      items,
    };
  }, [tenant, previewConfig]);

  const categories: string[] = ['Tous', ...Array.from(new Set<string>(faqConfig.items.map((faq: FAQItem) => faq.category)))];

  const toggleQuestion = (index: number) => {
    setOpenQuestions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const filteredFaqs: FAQItem[] = faqConfig.items.filter((faq: FAQItem) => {
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'Tous' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <PublicLayout>
      <SEOMetaTags
        title={`FAQ - ${tenant?.name || 'Notre site'}`}
        description={faqConfig.headerSubtitle}
        image={tenant?.logo || undefined}
        url={`${window.location.origin}/faq`}
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {faqTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: faqTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[faqTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(faqTheme.overlay.color1, faqTheme.overlay.opacity1)},
                    ${hexToRgba(faqTheme.overlay.color2, faqTheme.overlay.opacity2)},
                    ${hexToRgba(faqTheme.overlay.color3, faqTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !faqTheme.overlay.useGradient
                ? hexToRgba(faqTheme.overlay.color1, faqTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {faqTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(faqTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[faqTheme.icons[0].icon] || iconMap.HelpCircle;
                return IconComponent ? <IconComponent size={faqTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {faqTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(faqTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[faqTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={faqTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {faqTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(faqTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[faqTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={faqTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {faqTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(faqTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[faqTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={faqTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        {/* Contenu */}
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
            {/* En-tête */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl"
                style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d` }}
              >
                <HelpCircle className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{faqConfig.headerTitle}</span>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg"
                style={{ color: tenant?.config?.color3 || '#ffffff' }}
              >
                {faqConfig.headerTitle}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-lg drop-shadow-md max-w-3xl mx-auto"
                style={{ color: `${tenant?.config?.color3 || '#ffffff'}e6` }}
              >
                {faqConfig.headerSubtitle}
              </motion.p>
            </div>

            {/* Barre de recherche */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5" style={{ color: tenant?.config?.color4 || '#9ca3af' }} />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-4 bg-white/10 border border-white/20 rounded-lg placeholder-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                  style={{ 
                    color: tenant?.config?.color3 || '#ffffff',
                    '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                  } as React.CSSProperties}
                  placeholder={faqConfig.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Catégories */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full border transition-all duration-300 ${
                    activeCategory === category
                      ? 'font-semibold'
                      : 'border-white/10 hover:border-opacity-50'
                  }`}
                  style={activeCategory === category ? {
                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                    borderColor: tenant?.config?.color1 || '#fe8d17',
                    color: tenant?.config?.color3 || '#ffffff'
                  } : {
                    color: `${tenant?.config?.color3 || '#ffffff'}b3`,
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                  }}
                  onMouseEnter={(e) => {
                    if (activeCategory !== category) {
                      e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}66`;
                      e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeCategory !== category) {
                      e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                      e.currentTarget.style.color = `${tenant?.config?.color3 || '#ffffff'}b3`;
                    }
                  }}
                >
                  {category === 'Tous' ? category : (
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      {category}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Liste des FAQ */}
            <div className="max-w-3xl mx-auto">
              {filteredFaqs.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-lg" style={{ color: `${tenant?.config?.color3 || '#ffffff'}99` }}>
                    Aucune question ne correspond à votre recherche.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredFaqs.map((faq: FAQItem, index: number) => (
                    <div
                      key={index}
                      className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden transition-all shadow-lg hover:shadow-xl"
                      style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}33` }}
                    >
                      <button
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                        onClick={() => toggleQuestion(index)}
                      >
                        <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{faq.question}</span>
                        {openQuestions.includes(index) ? (
                          <ChevronUp className="w-5 h-5" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                        ) : (
                          <ChevronDown className="w-5 h-5" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                        )}
                      </button>
                      
                      <div
                        className={`px-6 transition-all duration-200 ease-in-out ${
                          openQuestions.includes(index)
                            ? 'max-h-96 py-4 opacity-100'
                            : 'max-h-0 py-0 opacity-0'
                        }`}
                      >
                        <p style={{ color: `${tenant?.config?.color3 || '#ffffff'}cc` }}>{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>     
       
      </div>
    </PublicLayout>
  );
};
